import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Package, ShoppingCart, FileText, Truck,
  Boxes, Bell, LogOut, ChevronDown, Users, Settings as SettingsIcon, Landmark,
  Menu, X, Database, BarChart3, AlertCircle, FileSpreadsheet, Tags, Award, Layers,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import BhawarLogo from "@/components/BhawarLogo";
import SamsungLogo from "@/components/SamsungLogo";

// Role groups
const ORDER_ROLES  = ["admin", "dealer"];          // Can place orders
const VIEW_ROLES   = ["sales", "manager", "accounts"]; // View-only staff
const ALL_ROLES    = [...ORDER_ROLES, ...VIEW_ROLES];
const STAFF_ROLES  = ["manager", "accounts", "admin"]; // Can see Manager Dashboard
const MAPPING_ROLES = ["accounts", "admin"];          // Can edit metadata

// Grouped Role-Based Navigation Architecture
const NAV_SECTIONS = [
  {
    title: "Overview & Analytics",
    items: [
      { to: "/",                   label: "Dashboard",            icon: LayoutDashboard, roles: ALL_ROLES },
      { to: "/dealer-analytics",   label: "Dealer Analytics",     icon: Landmark,        roles: ["sales", "manager", "accounts", "admin"] },
      { to: "/manager-dashboard",  label: "Mgr Dashboard",        icon: BarChart3,       roles: STAFF_ROLES },
    ],
  },
  {
    title: "Commerce & Inventory",
    items: [
      { to: "/catalogue",          label: "Catalogue",            icon: Package,         roles: ALL_ROLES },
      { to: "/po/new",             label: "New PO",               icon: ShoppingCart,    roles: ORDER_ROLES },
      { to: "/po",                 label: "Purchase Orders",      icon: FileText,        roles: ALL_ROLES },
      { to: "/so",                 label: "Sales Orders",         icon: Truck,           roles: ALL_ROLES },
      { to: "/inventory",          label: "Inventory",            icon: Boxes,           roles: ["sales","manager","accounts","admin"] },
    ],
  },
  {
    title: "Finance & Commercials",
    items: [
      { to: "/invoices",           label: "Tax E-Invoices",       icon: FileText,        roles: ALL_ROLES },
      { to: "/ledger",             label: "Customer Ledger",      icon: Landmark,        roles: ALL_ROLES },
      { to: "/outstanding",        label: "Outstanding Aging",    icon: AlertCircle,     roles: ALL_ROLES },
      { to: "/schemes",            label: "Schemes & Circulars",  icon: Award,           roles: ALL_ROLES },
    ],
  },
  {
    title: "Catalog & Pricing Hub",
    items: [
      { to: "/product-mapping",    label: "Product Mapping",      icon: Tags,            roles: MAPPING_ROLES, badgeKey: "unmapped_skus" },
      { to: "/price-list-mapping", label: "Price List Mapping",   icon: Layers,          roles: MAPPING_ROLES },
    ],
  },
  {
    title: "Administration & SAP",
    items: [
      { to: "/dealers",            label: "Dealers",              icon: Users,           roles: ["admin"] },
      { to: "/users",              label: "Users",                icon: Users,           roles: ["admin"] },
      { to: "/sap-sync",           label: "SAP Sync",             icon: Database,        roles: ["accounts", "admin"] },
      { to: "/settings",           label: "Settings",             icon: SettingsIcon,    roles: ALL_ROLES },
    ],
  },
];

const ROLE_BADGE = {
  admin:    "bg-rose-50 text-rose-700 border-rose-200",
  dealer:   "bg-blue-50 text-blue-700 border-blue-200",
  sales:    "bg-purple-50 text-purple-700 border-purple-200",
  manager:  "bg-amber-50 text-amber-800 border-amber-200",
  accounts: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [swipeStartX, setSwipeStartX] = useState(null);

  // Swipe-to-open: touch must start within 40px of left edge
  const handleGlobalTouchStart = (e) => {
    if (drawerOpen) return;
    const x = e.touches[0].clientX;
    if (x < 40) setSwipeStartX(x);
  };
  const handleGlobalTouchEnd = (e) => {
    if (swipeStartX === null) return;
    const x = e.changedTouches[0].clientX;
    if (x - swipeStartX > 75) setDrawerOpen(true);
    setSwipeStartX(null);
  };

  const loadNotifs = async () => {
    try { 
      const { data } = await api.get("/notifications"); 
      setNotifs(Array.isArray(data) ? data : []); 
    } catch (_) {
      setNotifs([]);
    }
  };
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 20000); return () => clearInterval(t); }, []);

  // Close mobile drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const unread = Array.isArray(notifs) ? notifs.filter(n => !n.read).length : 0;

  const markAllRead = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  const { data: unmappedCount = 0 } = useQuery({
    queryKey: ["unmapped-skus-count"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/products/unmapped-count");
        return data?.unmapped_count || 0;
      } catch {
        return 0;
      }
    },
    enabled: ["admin", "accounts"].includes(user?.role),
    staleTime: 1000 * 60 * 2,
  });

  const SidebarContent = (
    <div className="h-full w-full flex flex-col bg-slate-900 text-slate-200">
      <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center" data-testid="sidebar-bhawar-logo">
          <BhawarLogo height={48} tone="light" />
        </div>
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden p-1.5 rounded-md hover:bg-slate-800 text-slate-400"
          aria-label="Close menu"
          data-testid="close-drawer-btn"
        >
          <X size={18} />
        </button>
      </div>
      <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter((item) => {
            // For dealer analytics, only show to staff
            if (item.to === "/dealer-analytics" && user?.role === "dealer") return false;
            return item.roles.includes(user?.role);
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className="space-y-1">
              <div className="px-3 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">
                {section.title}
              </div>
              <div className="space-y-0.5">
                {visibleItems.map(({ to, label, icon: Icon, badgeKey }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g,"-")}`}
                    className={({ isActive }) => {
                      const active = isActive && !(to === "/po" && location.pathname.startsWith("/po/new"));
                      return `flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors duration-150 ${
                        active
                          ? "bg-blue-600/25 text-blue-200 border-l-2 border-blue-400 font-semibold"
                          : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100 border-l-2 border-transparent"
                      }`;
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon size={16} strokeWidth={1.75} />
                      <span>{label}</span>
                    </div>
                    {badgeKey === "unmapped_skus" && unmappedCount > 0 && (
                      <span className="bg-amber-500 text-slate-950 font-bold text-[10px] px-1.5 py-0.5 rounded-full">
                        {unmappedCount}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-500">
        <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
        <span>Online</span>
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
      onTouchStart={handleGlobalTouchStart}
      onTouchEnd={handleGlobalTouchEnd}
    >
      {/* Desktop sidebar (>= lg) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[240px] z-30">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          data-testid="mobile-drawer"
          onTouchStart={(e) => setTouchStartX(e.changedTouches[0].screenX)}
          onTouchEnd={(e) => {
            if (touchStartX !== null && touchStartX - e.changedTouches[0].screenX > 50) {
              setDrawerOpen(false);
            }
          }}
        >
          <div
            className="absolute inset-0 bg-black/40 transition-opacity duration-200"
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-backdrop"
          />
          <aside className="absolute inset-y-0 left-0 w-[240px] max-w-[85%] shadow-xl animate-in slide-in-from-left duration-200 bg-slate-900">
            {SidebarContent}
          </aside>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 lg:left-[240px] right-0 h-14 bg-white/90 backdrop-blur-xl border-b border-l border-slate-200 lg:border-l-0 z-20 flex items-center px-4 sm:px-6 gap-3">
        <button
          className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          data-testid="open-drawer-btn"
        >
          <Menu size={20} />
        </button>

        {/* Mobile-only brand */}
        <div className="lg:hidden flex items-center mt-1">
          <BhawarLogo height={32} tone="dark" />
        </div>

        {/* Desktop-only role hint */}
        <div className="hidden lg:block text-xs tracking-widest uppercase text-slate-500">
          {user?.role === "manager" && user?.area ? `Manager · ${user.area} region` : `${user?.role || ""}`}
        </div>

        <div className="hidden md:flex items-center gap-3 pl-4 border-l border-slate-200">
          <SamsungLogo size="md" tone="dark" />
          <span className="hidden xl:inline text-[10px] text-slate-400 tracking-widest uppercase">Authorised Distributor</span>
        </div>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="header-notif-btn">
                <Bell size={18} strokeWidth={1.5} />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 text-[10px] font-semibold bg-blue-600 text-white rounded-full min-w-[1.125rem] h-4 px-1 flex items-center justify-center leading-none shadow-sm">
                    {unread > 999 ? "999+" : unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[92vw] sm:w-80 p-0" align="end">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <div className="text-sm font-medium">Notifications</div>
                <button className="text-xs text-blue-600 hover:text-blue-800" onClick={markAllRead}>Mark all read</button>
              </div>
              <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                {notifs.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">No notifications</div>}
                {notifs.map(n => (
                  <button
                    key={n.notification_id}
                    onClick={() => { if (n.link) navigate(n.link); api.post(`/notifications/${n.notification_id}/read`).then(loadNotifs); }}
                    data-testid={`notif-${n.notification_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start justify-between gap-2 ${n.read ? "opacity-60 bg-white" : "bg-blue-50/40"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5 leading-snug">{n.message || n.body}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" title="Unread" />
                    )}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors" data-testid="header-user-btn">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">{user?.name?.[0]}</div>
                )}
                <span className="hidden sm:inline text-sm">{user?.name}</span>
                <Badge variant="outline" className={`hidden md:inline-flex text-[10px] uppercase tracking-wider ${ROLE_BADGE[user?.role] || ""}`}>{user?.role}</Badge>
                <ChevronDown size={14} className="hidden sm:inline" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="px-2 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
              <Link to="/settings" className="block px-2 py-2 text-sm hover:bg-slate-100 rounded-md" data-testid="user-menu-settings">Settings</Link>
              <button onClick={logout} className="w-full text-left flex items-center gap-2 px-2 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-md" data-testid="user-menu-logout">
                <LogOut size={14} /> Sign out
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* Main */}
      <main className="lg:ml-[240px] pt-14 min-h-screen relative">
        <div className="absolute inset-y-0 left-[-1px] w-[1px] bg-slate-900 hidden lg:block z-10" />
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
